import { useEffect, useMemo, useState } from 'react';
import useFF from 'common/hooks/settings/useFF';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useOverrideProps from './useOverrideProps';
import useEmptyStateType from './useEmptyStateType';
import useCheckTeamPDTStatus from 'views/root/views/home/hooks/useCheckTeamPDTStatus';
import useInitialization from './initialization/useInitialization';
import { TEAM_SPACE_TABS } from '../team-space.consts';
import usePdtIterations from '../../hooks/usePdtIterations';
import useProjectByTeamId from 'state/selectors/hooks/useProjectByTeamId';
import useTeamIterations from 'common/hooks/pm/useTeamIterations';
import { FETCH_STATUSES } from 'common/constants/constants';
import useStatePersistance from './useStatePersistance';
import useTeamSpaceTrackingEvents from './useTeamSpaceTrackingEvents';

const useTeamSpace = () => {
  const isEnabled = useFF(FEATURE_FLAGS_NAMES.isTeamSpaceEnabled);
  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeIteration, setActiveIteration] = useState(null);
  const [selectedPdtIteration, setSelectedPdtIteration] = useState(null);
  const [trendDuration, setTrendDuration] = useState(null);
  const trackingEvents = useTeamSpaceTrackingEvents({ selectedTab });

  const isRetroactive = [
    TEAM_SPACE_TABS.COMPLETED,
    TEAM_SPACE_TABS.TREND
  ].includes(selectedTab);

  const teamId = isEnabled ? selectedTeam?.id : null;
  const teamProject = useProjectByTeamId(teamId);
  const pdtId = teamProject?.pdt_project_id;

  const {
    activeIterations,
    pastIterations,
    status: iterationsStatus
  } = useTeamIterations(teamId);
  const { pdtIterations, status: pdtIterationsStatus } =
    usePdtIterations(pdtId);

  const isIterationsFetchFinished = useMemo(() => {
    return (
      iterationsStatus === FETCH_STATUSES.FINISHED &&
      pdtIterationsStatus === FETCH_STATUSES.FINISHED
    );
  }, [pdtIterationsStatus, iterationsStatus]);

  const { loadingPDTProject: isLoadingPdtStatus, teamPdtStatus: pdtStatus } =
    useCheckTeamPDTStatus(teamId);

  const isInitialized = useInitialization({
    setSelectedTab,
    setSelectedTeam,
    setSelectedPdtIteration,
    setActiveIteration,
    setTrendDuration,
    pastIterations,
    activeIterations,
    pdtIterations,
    isIterationsFetchFinished
  });

  useStatePersistance({ isInitialized, selectedTeam });

  const isLoading =
    [iterationsStatus, pdtIterationsStatus].includes(FETCH_STATUSES.LOADING) ||
    isLoadingPdtStatus;

  const emptyStateType = useEmptyStateType({
    isEnabled,
    isLoading,
    selectedTab,
    selectedTeam,
    pdtStatus,
    pdtIterations,
    isInitialized
  });

  const overrideProps = useOverrideProps({
    isEnabled,
    isLoading,
    selectedTab,
    setSelectedTab,
    selectedTeam,
    selectedPdtIteration,
    setSelectedPdtIteration,
    pdtIterations,
    activeIteration,
    trendDuration
  });

  useEffect(() => {
    if (!isInitialized) return;
    setActiveIteration(activeIterations[0] || null);
  }, [activeIterations]);

  useEffect(() => {
    if (!isInitialized) return;
    setSelectedPdtIteration(pdtIterations[0] || null);
  }, [pdtIterations]);

  useEffect(() => {
    if (!isInitialized) return;
    trackingEvents.visit();
  }, [isInitialized]);

  return {
    isEnabled,
    emptyStateType,
    isRetroactive,
    selectedTab,
    setSelectedTab,
    selectedTeam,
    setSelectedTeam,
    activeIteration,
    setActiveIteration,
    selectedPdtIteration,
    setSelectedPdtIteration,
    trendDuration,
    setTrendDuration,
    pdtIterations,
    activeIterations,
    overrideProps
  };
};

export default useTeamSpace;
